import { Vector3 } from 'three';
import type { SceneTopologyOwnerRuntimeResolverDependencies } from '../../../../../types/reference/sketch-entity-reference/SceneTopologyOwnerRuntimeResolver';
import { resolveImportedModelTopologyFeatureLine } from '../../../../imported-model/topology/imported-model-topology-runtime';
import type { FeatureLine } from '../../../../mesh/feature/FeatureLine';
import { FeatureResultMesh } from '../../../../mesh/feature/FeatureResultMesh';
import {isModelBodyMetadataInLineage} from '../../../../model-body/model-body-metadata';
import type { TopologyEdgeSourceDescriptor } from '../../model/ExternalGeometrySourceDescriptor';
import type { TopologyOwnerRuntimeHost } from '../../../../../types/reference/sketch-entity-reference/TopologyOwnerRuntimeResolver';
import {TopologyOwnerRuntimeHostFactory} from './TopologyOwnerRuntimeHostFactory';

export type {
	SceneTopologyOwnerRuntimeResolverDependencies,
} from '../../../../../types/reference/sketch-entity-reference/SceneTopologyOwnerRuntimeResolver';

/**
 * 从当前场景解析 topology owner 运行时宿主。
 *
 * 设计说明：
 * 1. 本类只把场景中的结果体与导入体适配为可枚举的边、面候选集合；
 * 2. 不拼装或修改持久化来源描述，也不决定最终重绑定目标；
 * 3. 稳定身份、邻接信号和几何指纹的最终选择仍由 `TopologyEdgeRelocationService` 负责。
 */
export class SceneTopologyOwnerRuntimeResolver {
	/** Scene/checkpoint 共用的 topology host 工厂。 */
	private readonly topologyHostFactory = new TopologyOwnerRuntimeHostFactory();
	/**
	 * 创建当前文档场景的 topology owner Resolver。
	 *
	 * @param dependencies 场景根节点与稳定对象读取能力。
	 */
	public constructor(
		private readonly dependencies: SceneTopologyOwnerRuntimeResolverDependencies,
	) {}

	/**
	 * 按来源描述解析当前场景中的 topology owner host。
	 *
	 * @param descriptor 持久化的 topology 边来源描述。
	 * @returns 命中当前 owner 时返回可重定位 host，否则返回 `null`。
	 */
	public resolve(descriptor: TopologyEdgeSourceDescriptor): TopologyOwnerRuntimeHost | null {
		return this.resolveFeatureResultHost(descriptor)
			?? this.resolveImportedHost(descriptor);
	}

	/**
	 * 只解析 Worker 已经确认的当前结果成员。
	 *
	 * 该入口只用于 live Convert Entities 查询返回 `resolvedResultObjectId` 后读取场景几何；
	 * 历史重定位仍使用 `resolve()` 枚举同一实体血缘的合法后代集合。
	 *
	 * @param descriptor 已携带 Worker 当前结果对象 ID 的 topology 来源。
	 * @returns 精确成员 host；对象缺失或与 owner/实体血缘冲突时返回 `null`。
	 */
	public resolveExactMember(
		descriptor: TopologyEdgeSourceDescriptor,
	): TopologyOwnerRuntimeHost | null {
		if (descriptor.owner.ownerKind !== 'feature-result') {
			return this.resolveImportedHost(descriptor);
		}
		const resultObjectId = descriptor.owner.resultObjectId?.trim() ?? '';
		if (!resultObjectId) {
			return null;
		}
		const exactMatches = this.collectFeatureResultOwnerMatches(descriptor)
			.filter(mesh => mesh.objectId === resultObjectId);
		if (exactMatches.length !== 1) {
			return null;
		}
		const exactMesh = exactMatches[0]!;
		const hasModelBodyLineage = Boolean(
			descriptor.owner.modelBodyId?.trim()
			|| descriptor.owner.modelBodyRevisionId?.trim(),
		);
		if (hasModelBodyLineage && !isModelBodyMetadataInLineage(
			exactMesh.getFeatureMetadata(),
			exactMesh.objectId,
			descriptor.owner.modelBodyId,
			descriptor.owner.modelBodyRevisionId,
		)) {
			return null;
		}
		const requestedBodyBindingId = descriptor.owner.bodyBindingId?.trim() ?? '';
		if (
			requestedBodyBindingId
			&& exactMesh.getFeatureMetadata().bodyBindingId !== requestedBodyBindingId
		) {
			return null;
		}
		return this.createFeatureResultMeshHost(exactMesh);
	}

	/**
	 * 解析特征结果体 owner。
	 *
	 * 先按 Feature owner 限定候选，再按稳定实体血缘保留原位实体与合法分裂成员。
	 * 边身份仍由下游重定位服务判断，禁止把同一 Feature 下的无关实体并入候选域。
	 *
	 * @param descriptor topology 边来源描述。
	 * @returns 特征结果体 host；未命中时返回 `null`。
	 */
	private resolveFeatureResultHost(
		descriptor: TopologyEdgeSourceDescriptor,
	): TopologyOwnerRuntimeHost | null {
		if (descriptor.owner.ownerKind !== 'feature-result') {
			return null;
		}
		const ownerMatches = this.collectFeatureResultOwnerMatches(descriptor);
		const hasModelBodyLineage = Boolean(
			descriptor.owner.modelBodyId?.trim()
			|| descriptor.owner.modelBodyRevisionId?.trim(),
		);
		const matchedMeshes = hasModelBodyLineage
			? ownerMatches.filter((mesh) => isModelBodyMetadataInLineage(
				mesh.getFeatureMetadata(),
				mesh.objectId,
				descriptor.owner.modelBodyId,
				descriptor.owner.modelBodyRevisionId,
			))
			: this.resolveLegacyFeatureBodyMatches(ownerMatches, descriptor);
		return matchedMeshes.length > 0
			? this.createFeatureResultMeshesHost(matchedMeshes)
			: null;
	}

	/**
	 * 收集同一 Feature owner 的场景结果体，不在此阶段裁决实体血缘或成员唯一性。
	 *
	 * @param descriptor topology 边来源描述。
	 * @returns 已去重的 Feature 结果体集合。
	 */
	private collectFeatureResultOwnerMatches(
		descriptor: TopologyEdgeSourceDescriptor,
	): FeatureResultMesh[] {
		const ownerMatches: FeatureResultMesh[] = [];
		const visitedMeshIds = new Set<string>();
		const visitMesh = (object: unknown): void => {
			if (!(object instanceof FeatureResultMesh) || visitedMeshIds.has(object.uuid)) {
				return;
			}
			visitedMeshIds.add(object.uuid);
			if (this.isMatchingFeatureResultOwner(object, descriptor)) {
				ownerMatches.push(object);
			}
		};

		if (descriptor.owner.resultObjectId) {
			visitMesh(this.dependencies.findSceneObjectById(descriptor.owner.resultObjectId));
		}
		this.dependencies.sceneRoot.traverse((object) => {
			visitMesh(object);
		});
		return ownerMatches;
	}

	/**
	 * 为缺少统一实体血缘的旧引用保守解析 Feature 结果体。
	 *
	 * bodyBindingId 精确命中时仍可使用；没有任何 body 身份时只接受唯一结果体，
	 * 禁止使用可能已漂移的 resultObjectId 在同一 Feature 的多个实体间猜测。
	 *
	 * @param ownerMatches 已按 Feature owner 过滤的结果体。
	 * @param descriptor 持久化来源描述。
	 * @returns 可安全使用的旧记录结果体集合。
	 */
	private resolveLegacyFeatureBodyMatches(
		ownerMatches: readonly FeatureResultMesh[],
		descriptor: TopologyEdgeSourceDescriptor,
	): FeatureResultMesh[] {
		const requestedBodyBindingId = descriptor.owner.bodyBindingId?.trim() ?? '';
		if (requestedBodyBindingId) {
			return ownerMatches.filter((mesh) => {
				const bodyBindingId = mesh.getFeatureMetadata().bodyBindingId;
				return typeof bodyBindingId === 'string'
					&& bodyBindingId === requestedBodyBindingId;
			});
		}
		return ownerMatches.length === 1 ? [ownerMatches[0]!] : [];
	}

	/**
	 * 判断结果体是否匹配来源 owner。
	 *
	 * @param mesh 当前候选结果体。
	 * @param descriptor topology 边来源描述。
	 * @returns 命中当前 owner 时返回 `true`。
	 */
	private isMatchingFeatureResultOwner(
		mesh: FeatureResultMesh,
		descriptor: TopologyEdgeSourceDescriptor,
	): boolean {
		if (descriptor.owner.ownerKind !== 'feature-result') {
			return false;
		}

		const metadata = mesh.getFeatureMetadata();
		/*
		 * Feature owner 必须由稳定 featureId 确认。resultObjectId 只用于快速找到场景对象，
		 * 后续 Feature 可能复用同一结果体 ID，不能据此把其他 Feature 并入当前 host。
		 */
		return metadata.featureId === descriptor.owner.ownerId;
	}

	/**
	 * 聚合同一源实体血缘下的多个结果体 host。
	 *
	 * @param ownerMeshes 已按 Feature 与实体血缘过滤的结果体集合。
	 * @returns 聚合后的可重定位 host。
	 */
	private createFeatureResultMeshesHost(
		ownerMeshes: readonly FeatureResultMesh[],
	): TopologyOwnerRuntimeHost {
		const hosts = ownerMeshes.map(ownerMesh => this.createFeatureResultMeshHost(ownerMesh));
		return this.topologyHostFactory.createCollection(hosts);
	}

	/**
	 * 把单个 FeatureResultMesh 适配为 topology owner host。
	 *
	 * @param ownerMesh 当前 owner 结果体。
	 * @returns 可供重定位服务消费的 host。
	 */
	private createFeatureResultMeshHost(ownerMesh: FeatureResultMesh): TopologyOwnerRuntimeHost {
		const topology = ownerMesh.getTopologySnapshot();
		if (!topology) {
			return {
				getEdgeByIndex: () => null,
				listEdges: () => [],
				getFaceByIndex: () => null,
				listFaces: () => [],
			};
		}
		ownerMesh.updateWorldMatrix(true, false);
		return this.topologyHostFactory.create({
			topology,
			topologyMemberId: ownerMesh.objectId,
			transformPoint: point => {
				const worldPoint = new Vector3(point[0], point[1], point[2])
					.applyMatrix4(ownerMesh.matrixWorld);
				return [worldPoint.x, worldPoint.y, worldPoint.z];
			},
			resolveFeatureLine: edgeIndex => (
				ownerMesh.getFeatureLineByIndex(edgeIndex)
				?? ownerMesh.getAllFeatureLines().find(candidate => candidate.edgeIndex === edgeIndex)
				?? null
			),
		});
	}

	/**
	 * 保守解析导入体或模型体 owner。
	 *
	 * 只有当前边能反查到完整 FeatureResultMesh host 时才允许枚举；禁止把单条旧索引边伪装为全量 host。
	 *
	 * @param descriptor topology 边来源描述。
	 * @returns 可枚举 owner host；缺少稳定 host 时返回 `null`。
	 */
	private resolveImportedHost(
		descriptor: TopologyEdgeSourceDescriptor,
	): TopologyOwnerRuntimeHost | null {
		if (
			descriptor.owner.ownerKind !== 'imported-model-body'
			&& descriptor.owner.ownerKind !== 'model-body'
		) {
			return null;
		}

		const ownerIds = [
			descriptor.owner.bodyBindingId,
			descriptor.owner.ownerId,
			descriptor.owner.resultObjectId,
		].filter((value, index, values): value is string => (
			typeof value === 'string'
			&& value.length > 0
			&& values.indexOf(value) === index
		));
		const featureLine = ownerIds
			.map(ownerId => resolveImportedModelTopologyFeatureLine(
				this.dependencies.sceneRoot,
				ownerId,
				descriptor.locator.edgeIndex,
			))
			.find((candidate): candidate is FeatureLine => Boolean(candidate)) ?? null;
		const ownerMesh = featureLine?.getOwningResultMesh?.() ?? null;
		return ownerMesh instanceof FeatureResultMesh
			? this.createFeatureResultMeshHost(ownerMesh)
			: null;
	}

}
